/**
 * Login return path (Phase 41A, G-12).
 *
 * A logged-out user who opens a protected deep link is sent to /login with the
 * path and query in router state (`ProtectedRoute`). `LoginPage` stores it here
 * just before `loginWithRedirect`, and `CallbackPage` consumes it once the
 * round trip to Auth0 has finished, so sign-in lands where the user started
 * instead of on /dashboard.
 *
 * sessionStorage, not Auth0 `appState`: auth0-react 2.11's default
 * `onRedirectCallback` passes `appState.returnTo` to `window.history.replaceState`,
 * which changes the URL behind React Router's back. sessionStorage is per tab
 * and survives the cross-origin round trip; auth0-spa-js keeps its own login
 * transaction there by default, so this adds no new storage dependency.
 *
 * Every value is re-validated when it is read back. Storage can be edited from
 * DevTools or by any same-origin script, and React Router's `push` falls back
 * to `window.location.assign` when `pushState` rejects a cross-origin URL, so
 * an unvalidated path here is an open redirect.
 */

const STORAGE_KEY = 'guild.returnTo';
const MAX_PATH_LENGTH = 2048;
const MAX_AGE_MS = 30 * 60 * 1000;

/**
 * True when `p` is a same-origin path that is safe to navigate to after sign-in.
 *
 * Rejects:
 * - anything that is not a string starting with `/`, or is longer than 2048 chars;
 * - `//…` and `/\…`, which the browser resolves as a URL on another host;
 * - a tab, LF or CR anywhere: the URL parser deletes them before parsing, so
 *   `/<TAB>/evil.com` resolves as `//evil.com`;
 * - a backslash anywhere: React Router resolves `.`/`..` segments before the
 *   browser parses the URL, so `/./\evil.com` becomes `/\evil.com`, which the
 *   browser reads as `//evil.com`;
 * - /login and /callback, including with a query, hash, trailing slash or other
 *   letter case (React Router renders the same page for those).
 */
export function isSafeInAppPath(p: unknown): p is string {
  if (typeof p !== 'string' || p.length > MAX_PATH_LENGTH) return false;
  if (!p.startsWith('/') || p.startsWith('//') || p.startsWith('/\\')) return false;
  if (/[\t\n\r\\]/.test(p)) return false;
  const route = p.split(/[?#]/, 1)[0].replace(/\/+$/, '').toLowerCase();
  return route !== '/login' && route !== '/callback';
}

/** Stores a safe return path for this tab. Unsafe paths and storage failures are ignored. */
export function rememberReturnTo(p: string | null): void {
  if (!isSafeInAppPath(p)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ p, at: Date.now() }));
  } catch {
    // Storage unavailable (private mode, blocked site data): sign-in lands on /dashboard.
  }
}

/**
 * Returns the stored return path once, then forgets it. Returns null when
 * nothing is stored, storage is unavailable, or the entry is malformed, is
 * 30 minutes old or more, or holds an unsafe path.
 */
export function consumeReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const entry: unknown = JSON.parse(raw);
    if (typeof entry !== 'object' || entry === null) return null;
    const { p, at } = entry as { p?: unknown; at?: unknown };
    if (typeof at !== 'number') return null;
    const age = Date.now() - at;
    return age >= 0 && age < MAX_AGE_MS && isSafeInAppPath(p) ? p : null;
  } catch {
    return null;
  }
}
